import { useEffect } from "react";

/**
 * useSeoHead — manipuliert <head> dynamisch für SPA-Seiten.
 * Setzt title, meta description, meta keywords, canonical, og:* tags
 * und fügt optional JSON-LD scripts ein. Beim Unmount werden Original-Werte
 * wiederhergestellt (für saubere Navigation zurück zur Home-Seite).
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {string} props.description
 * @param {string} [props.keywords]
 * @param {string} props.canonical - vollständige URL (https://feaststar.app/...)
 * @param {string} [props.ogImage]
 * @param {string} [props.locale="de_DE"]
 * @param {Array<Object>} [props.jsonLd] - Array von strukturierten Daten Objekten
 * @param {Array<{hreflang:string,href:string}>} [props.alternates]
 */
export function useSeoHead({
  title,
  description,
  keywords,
  canonical,
  ogImage = "https://customer-assets.emergentagent.com/job_feastar-customer/artifacts/moswimug_Feaststar%20Final.png",
  locale = "de_DE",
  jsonLd = [],
  alternates = [],
}) {
  useEffect(() => {
    if (typeof document === "undefined") return;

    // Backup
    const original = {
      title: document.title,
      description: getMeta("name", "description"),
      keywords: getMeta("name", "keywords"),
      canonical: getLink("canonical"),
      ogTitle: getMeta("property", "og:title"),
      ogDescription: getMeta("property", "og:description"),
      ogUrl: getMeta("property", "og:url"),
      ogImage: getMeta("property", "og:image"),
      ogLocale: getMeta("property", "og:locale"),
      twitterTitle: getMeta("name", "twitter:title"),
      twitterDescription: getMeta("name", "twitter:description"),
      twitterImage: getMeta("name", "twitter:image"),
    };

    // Apply
    if (title) document.title = title;
    if (description) setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);
    if (canonical) setLink("canonical", canonical);
    if (title) setMeta("property", "og:title", title);
    if (description) setMeta("property", "og:description", description);
    if (canonical) setMeta("property", "og:url", canonical);
    if (ogImage) setMeta("property", "og:image", ogImage);
    setMeta("property", "og:locale", locale);
    if (title) setMeta("name", "twitter:title", title);
    if (description) setMeta("name", "twitter:description", description);
    if (ogImage) setMeta("name", "twitter:image", ogImage);

    // Alternates (hreflang)
    const addedAlternates = [];
    if (alternates.length) {
      // remove existing dynamic alternates
      document
        .querySelectorAll('link[data-seo-dyn-alt="1"]')
        .forEach((el) => el.parentNode.removeChild(el));
      alternates.forEach((alt) => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = alt.hreflang;
        link.href = alt.href;
        link.setAttribute("data-seo-dyn-alt", "1");
        document.head.appendChild(link);
        addedAlternates.push(link);
      });
    }

    // JSON-LD
    const addedScripts = [];
    jsonLd.forEach((data) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-dyn", "1");
      script.text = JSON.stringify(data);
      document.head.appendChild(script);
      addedScripts.push(script);
    });

    return () => {
      // Restore
      document.title = original.title;
      setMeta("name", "description", original.description);
      setMeta("name", "keywords", original.keywords);
      setLink("canonical", original.canonical);
      setMeta("property", "og:title", original.ogTitle);
      setMeta("property", "og:description", original.ogDescription);
      setMeta("property", "og:url", original.ogUrl);
      setMeta("property", "og:image", original.ogImage);
      setMeta("property", "og:locale", original.ogLocale);
      setMeta("name", "twitter:title", original.twitterTitle);
      setMeta("name", "twitter:description", original.twitterDescription);
      setMeta("name", "twitter:image", original.twitterImage);
      addedScripts.forEach((s) => {
        if (s.parentNode) s.parentNode.removeChild(s);
      });
      addedAlternates.forEach((a) => {
        if (a.parentNode) a.parentNode.removeChild(a);
      });
    };
  }, [title, description, keywords, canonical, ogImage, locale, JSON.stringify(jsonLd), JSON.stringify(alternates)]);
}

function getMeta(attr, value) {
  const el = document.querySelector(`meta[${attr}="${value}"]`);
  return el ? el.getAttribute("content") : "";
}
function setMeta(attr, value, content) {
  if (content === undefined || content === null) return;
  let el = document.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}
function getLink(rel) {
  const el = document.querySelector(`link[rel="${rel}"]`);
  return el ? el.getAttribute("href") : "";
}
function setLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
